/**
 * Lucent Carat Lab - Luxury Predictive Search Popup Modal
 * Handles:
 * - Fullscreen overlay modal with slide-down animation
 * - Debounced predictive search via Shopify's /search/suggest.json API
 * - Instant product card rendering with high-res thumbnails & prices
 * - Popular search pills & quick collection navigation
 * - Keyboard shortcuts (Escape to close, autofocus on open)
 */

(function () {
  'use strict';

  let debounceTimer = null;

  window.LucentSearch = {
    modal: null,
    input: null,
    clearBtn: null,
    initialView: null,
    resultsView: null,
    grid: null,
    loadingEl: null,
    noResultsEl: null,
    countEl: null,
    viewAllLink: null,

    init: function () {
      const self = this;
      this.modal = document.getElementById('LucentSearchModal');
      if (!this.modal) return;

      this.input = this.modal.querySelector('[data-search-input]');
      this.clearBtn = this.modal.querySelector('[data-search-clear]');
      this.initialView = this.modal.querySelector('[data-search-initial]');
      this.resultsView = this.modal.querySelector('[data-search-results]');
      this.grid = this.modal.querySelector('[data-search-grid]');
      this.loadingEl = this.modal.querySelector('[data-search-loading]');
      this.noResultsEl = this.modal.querySelector('[data-search-no-results]');
      this.countEl = this.modal.querySelector('[data-search-count]');
      this.viewAllLink = this.modal.querySelector('[data-search-view-all]');

      // Triggers (header search icon & mobile links)
      document.addEventListener('click', function (e) {
        const trigger = e.target.closest('[data-search-popup-trigger], .lj-action--search, a[aria-label="Search"]');
        if (trigger) {
          e.preventDefault();
          self.open();
        }

        // Close triggers
        if (
          e.target.matches('[data-search-close]') ||
          e.target.closest('[data-search-close]') ||
          e.target.classList.contains('lucent-search-modal__overlay')
        ) {
          e.preventDefault();
          self.close();
        }

        // Trending search pills
        const tag = e.target.closest('[data-search-tag]');
        if (tag) {
          e.preventDefault();
          const term = tag.getAttribute('data-search-tag');
          if (self.input) {
            self.input.value = term;
            self.input.focus();
            self.handleInput(term);
          }
        }
      });

      // Search input events
      if (this.input) {
        this.input.addEventListener('input', function () {
          self.handleInput(self.input.value);
        });

        this.input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            const val = self.input.value.trim();
            if (val) {
              window.location.href = '/search?q=' + encodeURIComponent(val);
            }
          }
        });
      }

      // Clear button
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          if (self.input) {
            self.input.value = '';
            self.input.focus();
            self.handleInput('');
          }
        });
      }

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal.classList.contains('is-open')) {
          self.close();
        }
      });
    },

    open: function () {
      if (!this.modal) return;
      this.modal.classList.add('is-open');
      document.body.classList.add('search-modal-open');
      if (this.input) {
        const self = this;
        setTimeout(function () {
          self.input.focus();
          if (self.input.value) {
            self.handleInput(self.input.value);
          }
        }, 150);
      }
    },

    close: function () {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      document.body.classList.remove('search-modal-open');
    },

    handleInput: function (val) {
      const self = this;
      const query = (val || '').trim();

      // Clear button visibility
      if (this.clearBtn) {
        if (query.length > 0) {
          this.clearBtn.classList.add('is-visible');
        } else {
          this.clearBtn.classList.remove('is-visible');
        }
      }

      if (query.length === 0) {
        this.showInitial();
        return;
      }

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        self.fetchSuggestions(query);
      }, 220);
    },

    showInitial: function () {
      if (this.initialView) this.initialView.style.display = 'flex';
      if (this.resultsView) this.resultsView.classList.remove('is-active');
      if (this.loadingEl) this.loadingEl.classList.remove('is-active');
      if (this.noResultsEl) this.noResultsEl.classList.remove('is-active');
    },

    fetchSuggestions: function (query) {
      const self = this;
      if (this.initialView) this.initialView.style.display = 'none';
      if (this.resultsView) this.resultsView.classList.remove('is-active');
      if (this.noResultsEl) this.noResultsEl.classList.remove('is-active');
      if (this.loadingEl) this.loadingEl.classList.add('is-active');

      const url =
        '/search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product&resources[options][unavailable_products]=hide';

      fetch(url)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (self.loadingEl) self.loadingEl.classList.remove('is-active');

          const products =
            data && data.resources && data.resources.results && data.resources.results.products
              ? data.resources.results.products
              : [];

          self.renderResults(products, query);
        })
        .catch(function (err) {
          console.error('LucentSearch suggest error', err);
          if (self.loadingEl) self.loadingEl.classList.remove('is-active');
          if (self.noResultsEl) self.noResultsEl.classList.add('is-active');
        });
    },

    renderResults: function (products, query) {
      if (!this.resultsView || !this.grid) return;

      if (!products || products.length === 0) {
        this.resultsView.classList.remove('is-active');
        if (this.noResultsEl) {
          this.noResultsEl.classList.add('is-active');
          const termSpan = this.noResultsEl.querySelector('[data-no-results-term]');
          if (termSpan) termSpan.textContent = query;
        }
        return;
      }

      if (this.noResultsEl) this.noResultsEl.classList.remove('is-active');
      this.resultsView.classList.add('is-active');

      if (this.countEl) {
        this.countEl.innerHTML =
          'Found <strong>' + products.length + '</strong> ' +
          (products.length === 1 ? 'piece' : 'pieces') +
          ' for <em>"' + escapeHtml(query) + '"</em>';
      }

      if (this.viewAllLink) {
        this.viewAllLink.href = '/search?q=' + encodeURIComponent(query);
      }

      let html = '';
      products.forEach(function (p) {
        const imageSrc = p.image || (p.featured_image && p.featured_image.url) || '';
        const imgMarkup = imageSrc
          ? `<img src="${imageSrc}" alt="${escapeHtml(p.title)}" loading="lazy" width="300" height="300" />`
          : `<div style="width:100%;height:100%;background:#f5f5f5;display:flex;align-items:center;justify-content:center;"><svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#ccc"><circle cx="10.5" cy="10.5" r="6.7"></circle><path d="M15.6 15.6L21 21"></path></svg></div>`;

        // Format price
        let priceStr = '';
        if (p.price) {
          const numPrice = parseFloat(p.price);
          if (!isNaN(numPrice)) {
            priceStr = formatMoney(numPrice);
          } else {
            priceStr = p.price;
          }
        }

        html += `
          <a href="${p.url}" class="lucent-search-card">
            <div class="lucent-search-card__media">
              ${imgMarkup}
            </div>
            <div class="lucent-search-card__info">
              <h4 class="lucent-search-card__title">${escapeHtml(p.title)}</h4>
              <div class="lucent-search-card__price">${priceStr}</div>
            </div>
          </a>
        `;
      });

      this.grid.innerHTML = html;
    }
  };

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(centsOrDollars) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const cents = centsOrDollars < 10000 && !Number.isInteger(centsOrDollars)
        ? Math.round(centsOrDollars * 100)
        : Math.round(centsOrDollars * 100);
      return window.Shopify.formatMoney(cents);
    }
    const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    try {
      if (activeCurrency) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: activeCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(centsOrDollars);
      }
    } catch (e) {}
    return (activeCurrency ? activeCurrency + ' ' : '') + Number(centsOrDollars).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.LucentSearch.init();
    });
  } else {
    window.LucentSearch.init();
  }
})();
